<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-10">
          <v-card-actions>
            <v-row>
              <v-col cols="3">
                <v-text-field
                  v-model="search"
                  placeholder="Key in company name here"
                  append-icon="search"
                  label="Search Company"
                  hide-no-data
                  hide-selected
                  clearable 
                />
              </v-col>

              <v-col cols="2">
                <v-select
                  v-model="statusFilter"
                  :items="items"
                  item-text="state"
                  item-value="value"
                  label="Company's Status"
                />
              </v-col>

              <v-col cols="3">
                <v-select
                  v-model="organizationFilter"
                  :items="companyGroups"
                  item-text="description1"
                  item-value="lookup_uid"
                  label="Company's Group"
                  clearable
                />
              </v-col>

              <v-col
                cols="2"
                offset-md="2"
              >
                <setup-companies-dialog-create
                  v-if="$hasPermissionTo(['create companies'])"
                  :company-groups="companyGroups"
                  :companies="combinedCompanies"
                  @created="refreshData"
                />
              </v-col>
            </v-row>
          </v-card-actions>

          <v-data-table
            :headers="filteredHeaders"
            :items="filteredCompanies"
            :loading="loading"
            :single-expand="true"
            :search="search"
            show-expand
          >
            <template v-slot:item="props">
              <tr>
                <td :class="props.isExpanded?'remove-bottom-border selected':'border-bottom'">
                  <v-btn
                    v-if="$hasPermissionTo(['view branches']) || $hasPermissionTo(['view cost centers']) || $hasPermissionTo(['view company banks'])"
                    icon
                    @click="props.expand(!props.isExpanded);"
                  >
                    <v-icon small> 
                      {{ props.isExpanded ? 'fa-chevron-up':'fa-chevron-down' }} 
                    </v-icon>
                  </v-btn>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected':'border-bottom' ">
                  <span>
                    {{ props.item.name }}
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected text-left':'border-bottom text-left' ">
                  <span>
                    {{ props.item.formerly_known?props.item.formerly_known:'N/A' }}
                  </span>
                </td>
                <td :class="props.isExpanded? 'remove-bottom-border selected text-center':'border-bottom text-center' ">
                  <span>
                    {{ props.item.registration_no?props.item.registration_no:'N/A' }}
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected text-center':'border-bottom text-center' ">
                  <span>
                    {{ props.item.old_registration_no?props.item.old_registration_no:'N/A' }}
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected text-center':'border-bottom text-center' ">
                  <span>
                    <v-icon
                      v-if="props.item.is_active"
                      color="green"
                    >
                      mdi-check-outline
                    </v-icon>
                    <v-icon
                      v-else
                      color="error"
                    >
                      mdi-close-outline
                    </v-icon>
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected':'border-bottom' ">
                  <span>
                    {{ constructAddress(props.item).toUpperCase() }}
                  </span>
                </td>

                <td :class="props.isExpanded? 'remove-bottom-border selected':'border-bottom' ">
                  <span v-if="statusFilter">
                    <setup-companies-dialog-edit
                      v-if="$hasPermissionTo(['update companies'])"
                      :company="props.item"
                      :company-groups="companyGroups"
                      :companies="combinedCompanies"
                      @updated="refreshData"
                    />
                    <setup-companies-dialog-delete
                      v-if="$hasPermissionTo(['delete companies'])"
                      :company="props.item"
                      @deleted="refreshData"
                    />
                  </span>
                  <span v-else>
                    <setup-companies-dialog-restore
                      v-if="$hasPermissionTo(['restore companies'])"
                      :company="props.item"
                      @restored="refreshData"
                    />
                  </span>
                </td>
              </tr>
            </template>

            <template v-slot:expanded-item="{ headers,item }">
              <td
                :colspan="headers.length"
                class="pb-2"
                style="background-color: rgba(0, 0, 0, .05);"
              >
                <setup-companies-expand-view
                  v-if="$hasPermissionTo(['view branches']) || $hasPermissionTo(['view cost centers']) || $hasPermissionTo(['view company banks'])"
                  :company="item"
                />
              </td>
            </template>
          </v-data-table>
        </v-card> 
      </v-col>
    </v-row>    
  </v-container>
</template>

<script>
  export default {  
    components: {
      SetupCompaniesDialogCreate: () => import('@/components/hris/setup/companies/DialogCreate'),
      SetupCompaniesDialogDelete: () => import('@/components/hris/setup/companies/DialogDelete'),
      SetupCompaniesDialogEdit: () => import('@/components/hris/setup/companies/DialogEdit'),
      SetupCompaniesDialogRestore: () => import('@/components/hris/setup/companies/DialogRestore'),
      SetupCompaniesExpandView: () => import('@/components/hris/setup/companies/ExpandView'),
    },
    metaInfo () {
      return {
        title: 'Setup Company',
      }
    },
    data () {
      return {
        companies: [],
        headers: [
          {
            text: 'Company',
            align: 'left',
            value: 'name',
          },
          {
            text: 'Formerly Known',
            align: 'left',
            value: 'formerly_known',
          },
          {
            text: 'Registration No.',
            align: 'center',
            value: 'registration_no',
          },
          {
            text: 'Old Registration No.',
            align: 'center',
            value: 'old_registration_no',
          },
          {
            text: 'Active?',
            align: 'center',
            value: 'is_active',
          },
          {
            text: 'Address',
            align: 'center',
            value: 'address',
          },
        ],
        search: '',
        loading: false,
        statusFilter: { state: 'Active', value: true },
        items: [
          { state: 'Active', value: true },
          { state: 'Deleted', value: false },
        ],
        trashedCompanies: [],
        panel: [0],
        companyGroups: [],
        organizationFilter: '',
      }
    },

    computed: {
      filteredHeaders () {
        return this.canEditOrDelete() ? [...this.headers, {
          text: '',
          align: 'center',
          value: 'action',
          sortable: false,
          width: '110px',
        },
        ] : this.headers
      },
      filteredCompanies () {
        let companies = this.statusFilter ? this.companies : this.trashedCompanies

        if (this.organizationFilter) {
          companies = companies.filter(company => company.organization_lookup_uid === this.organizationFilter)
        }

        return companies
      },
      combinedCompanies () {
        return this.companies.concat(this.trashedCompanies)
      },
    },

    created () {
      this.refreshData()
    },
    methods: {
      canEditOrDelete () {
        return this.$hasPermissionTo(['update companies']) || this.$hasPermissionTo(['delete companies']) || this.$hasPermissionTo(['restore companies'])
      },
      loadCompanies () {
        this.loading = true

        this.$api.hrisCompanies().list()
          .then(res => {
            this.companies = _.orderBy(res.data, ['name'], ['asc'])

            this.loading = false
          })
      },
      loadTrashedCompanies () {
        this.$api.hrisCompanies().listTrashed()
          .then(response => {
            this.trashedCompanies = _.orderBy(response.data, ['name'], ['asc'])
          })
      },
      loadCompanyGroupLookups () {
        this.$api.lookups().list('company_organization')
          .then(response => {
            this.companyGroups = _.orderBy(response.data, ['name'], ['asc'])
          })
      },
      refreshData () {
        this.loadCompanies()
        this.loadTrashedCompanies()
        this.loadCompanyGroupLookups()
      },

      constructAddress (item) {
        let constructedAddress = (item.address_line_one ? item.address_line_one + ', ' : '') + (item.address_line_two ? item.address_line_two + ', ' : '') + 
          (item.address_line_three ? item.address_line_three + ', ' : '') + (item.postcode ? item.postcode + ', ' : '') +
          (item.city ? (item.city || {}).name + ', ' : '') + (item.city ? ((item.city || {}).state || {}).name : '') 

        return constructedAddress.length > 0 ? constructedAddress : 'N/A'
      },
    },
  }
</script>

<style scoped>
.selected{
  background-color: rgba(0, 0, 0, .05);
}

.remove-bottom-border {
    border-bottom: 0px !important
}

.border-bottom {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important
}

.font-calibri{
    font-family: Calibri !important;
}

.font-16{
    font-size:16px;
}

</style>
